import { of as observableOf, Observable, forkJoin } from 'rxjs'
import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { AppSelectComponent } from 'common-components'
import { AddressComponent } from '@exchange/ui/shared/controls/address/address.component'
import {
  IProvider,
  NpiTypeODataService,
  INpiType,
  ProviderODataService,
  timezones,
  IProviderType,
  ProviderTypeODataService,
  ProviderStateODataService,
  ProviderAddressODataService
} from '@exchange/domain/.'
import { Option } from 'common-components'
import { UtilService } from '@exchange/services/.'
import { mergeMap, map, switchMap } from 'rxjs/operators'
import { LocationCoverageTypes } from '@exchange/app/domain/system/locationCoverageType'

@Component({
  selector: 'app-providers-edit-demographics',
  templateUrl: './providers-edit-demographics.component.html',
  standalone: false
})
export class ProvidersEditDemographicsComponent implements OnInit {
  get model(): IProvider {
    return this._model
  }

  @Input()
  set model(m: IProvider) {
    this._model = m
    this.setSelectedProviderType()
    this.setSelectedNpiType()
    this.setSelectedTimeZone()
    if (m.LocationCoverageAddressId && this.showConnectGeographicToggle()) {
      this.geographicToggle = true
    }
  }

  constructor(
    private providerTypeService: ProviderTypeODataService,
    private npiTypeService: NpiTypeODataService,
    private odata: ProviderODataService,
    public util: UtilService,
    public stateService: ProviderStateODataService,
    private providerService: ProviderODataService,
    private providerAddressService: ProviderAddressODataService
  ) {}
  @ViewChild('physicalAddress', { static: true }) physicalAddress: AddressComponent
  @ViewChild('mailingAddress', { static: true }) mailingAddress: AddressComponent
  @ViewChild('locationCoverageAddress') locationCoverageAddress: AddressComponent
  @ViewChild('providerType', { static: true }) providerType: AppSelectComponent
  @ViewChild('npiType', { static: true }) npiType: AppSelectComponent
  @ViewChild('timezone', { static: true }) timezone: AppSelectComponent

  private _model: IProvider = {} as IProvider
  timezones: string[] = []
  timezoneOptions: Option[] = []

  providerTypes: IProviderType[]
  providerTypeOptions: Option[]
  npiTypes: INpiType[]
  npiTypeOptions: Option[]
  geographicToggle = false
  oldLocationCoverageId: number = null

  numberOnly(event): boolean {
    const charCode = event.which ? event.which : event.keyCode
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false
    }
    return true
  }

  ngOnInit() {
    this.initializeTimeZones()
    this.initializeProviderTypes()
    this.initializeNpiTypes()
  }

  setSelectedProviderType() {
    if (this.model.ProviderTypeId && this.providerTypeOptions) {
      this.providerType.active = this.providerTypeOptions.filter(
        x => x.id === this.model.ProviderTypeId
      )
    }
  }

  setSelectedNpiType() {
    if (this.model.NpiTypeId && this.npiTypeOptions) {
      this.npiType.active = this.npiTypeOptions.filter(x => x.id === this.model.NpiTypeId)
    }
  }

  setSelectedTimeZone() {
    if (!this.model.IanaTimeZone) {
      this.model.IanaTimeZone = 'US/Central'
    }
    if (this.timezoneOptions) {
      this.timezone.active = this.timezoneOptions.filter(x => x.id === this.model.IanaTimeZone)
    }
  }

  private initializeTimeZones() {
    this.timezones = timezones
    this.timezoneOptions = timezones.map(x => new Option(x, x, x))

    this.setSelectedTimeZone()
  }

  private initializeNpiTypes() {
    this.npiTypeService
      .query()
      .exec()
      .subscribe(response => {
        this.npiTypes = response.data as INpiType[]
        this.npiTypeOptions = this.npiTypes.map(x => new Option(x.Id, x.Value, x))

        this.setSelectedNpiType()
      })
  }

  private initializeProviderTypes() {
    this.providerTypeService
      .query()
      .orderBy('Value')
      .exec()
      .subscribe(response => {
        this.providerTypes = response.data as IProviderType[]
        this.providerTypeOptions = this.providerTypes.map(x => new Option(x.Id, x.Value, x))
        this.setSelectedProviderType()
      })
  }

  public validate() {
    this.beforeValidation()

    const validationErrors = []

    if (!this.model.Name) {
      validationErrors.push('Name is required.')
    }

    if (!this.model.Npi) {
      validationErrors.push('NPI is required.')
    }

    if (!this.model.NpiTypeId) {
      validationErrors.push('NPI Type is required.')
    }

    if (!this.model.ProviderTypeId) {
      validationErrors.push('Provider Type is required.')
    }

    if (this.model.Abbreviation && this.model.Abbreviation.length > 5) {
      validationErrors.push('Abbreviation must be 5 or fewer characters')
    }

    if (
      (this.model.PeopleSoftLocationId && !this.model.PeopleSoftVendorId) ||
      (!this.model.PeopleSoftLocationId && this.model.PeopleSoftVendorId)
    ) {
      const missingText = !this.model.PeopleSoftLocationId
        ? 'PeopleSoft Location Id'
        : 'PeopleSoft Vendor Id'

      validationErrors.push(
        `When entering a PeopleSoft Location Id or Vendor Id, they are both required. Please add a value for ${missingText} to continue.`
      )
    }

    if (this.model.PeopleSoftLocationId === '000') {
      validationErrors.push(
        'Changes not saved, PeopleSoft Location ID value cannot be 000 for provider.'
      )
    }

    return validationErrors.concat(
      this.mailingAddress.validate(this.model.IsAnchor),
      this.physicalAddress.validate(this.model.IsAnchor)
    )
  }

  hasPayerAddressData(): boolean {
    return !!(
      this.model.PayerAddressConfiguration &&
      (this.model.PayerAddressConfiguration.AddressLine1 ||
        this.model.PayerAddressConfiguration.AddressLine2 ||
        this.model.PayerAddressConfiguration.StateCode ||
        this.model.PayerAddressConfiguration.CityName ||
        this.model.PayerAddressConfiguration.PostalCode)
    )
  }

  hasBankingConfigurationData(): boolean {
    return !!(
      this.model.BankingConfiguration &&
      (this.model.BankingConfiguration.BPR07PayerRoutingNumber ||
        this.model.BankingConfiguration.BPR10PayerIdentifier ||
        this.model.BankingConfiguration.BPR09PayerAccountNumber ||
        this.model.BankingConfiguration.BPR13PayeeRoutingNumber ||
        this.model.BankingConfiguration.BPR15PayeeAccountNumber)
    )
  }

  hasInterchangeConfigurationData(): boolean {
    return !!(
      this.model.InterchangeConfiguration &&
      (this.model.InterchangeConfiguration.ISA05SenderIdQualifier ||
        this.model.InterchangeConfiguration.ISA06SenderId ||
        this.model.InterchangeConfiguration.ISA07ReceiverIdQualifier ||
        this.model.InterchangeConfiguration.ISA08ReceiverId)
    )
  }

  hasPayerAdditionalInformationConfigData(): boolean {
    return !!(
      this.model.PayerAdditionalInformationConfiguration &&
      (this.model.PayerAdditionalInformationConfiguration.ReferenceIdentificationQualifier ||
        this.model.PayerAdditionalInformationConfiguration.ReferenceIdentification)
    )
  }

  hasPayerTechnicalContactInformationConfigData(): boolean {
    return !!(
      this.model.PayerTechnicalContactInformationConfiguration &&
      (this.model.PayerTechnicalContactInformationConfiguration.CommunicationNumber ||
        this.model.PayerTechnicalContactInformationConfiguration.CommunicationNumberExtension)
    )
  }

  showConnectGeographicToggle(): boolean {
    return (
      this.model.Market &&
      this.model.Market.LocationCoverageTypeId === LocationCoverageTypes.Distance
    )
  }

  public saveProvider([pa, ma, lca]): Observable<IProvider> {
    if (this.model.LocationCoverageAddressId && !this.geographicToggle) {
      this.oldLocationCoverageId = this.model.LocationCoverageAddressId
    }

    this.model.PhysicalAddressId = pa ? +pa.Id : null
    this.model.MailingAddressId = ma ? +ma.Id : null
    this.model.LocationCoverageAddressId = lca ? +lca.Id : null
    const mailingAddressFilter = this.model.MailingAddress
      ? `id eq ${this.model.MailingAddress.StateId}`
      : null
    return this.stateService
      .query()
      .filter(mailingAddressFilter)
      .orderBy('Abbreviation')
      .exec()
      .pipe(
        map(response => {
          return {
            Id: +this.model.Id || undefined,
            Name: this.model.Name,
            Abbreviation: this.model.Abbreviation,
            OrganizationNameOverride: this.model.OrganizationNameOverride,
            Npi: this.model.Npi,
            NpiTypeId: this.model.NpiTypeId,
            PhoneNumber: this.util.sanitizePhoneNumber(this.model.PhoneNumber),
            PhoneNumber1Id: this.model.PhoneNumber1Id || null,
            PhoneNumber1: this.model.PhoneNumber1 || null,
            Local911PhoneNumber: this.util.sanitizePhoneNumber(this.model.Local911PhoneNumber),
            Local911PhoneNumber1Id: this.model.Local911PhoneNumber1Id || null,
            Local911PhoneNumber1: this.model.Local911PhoneNumber1 || null,
            OrganizationPhoneNumberOverride: this.util.sanitizePhoneNumber(
              this.model.OrganizationPhoneNumberOverride
            ),
            OrganizationPhoneNumberOverride1Id:
              this.model.OrganizationPhoneNumberOverride1Id || null,
            OrganizationPhoneNumberOverride1: this.model.OrganizationPhoneNumberOverride1 || null,
            EmailAddress: this.model.EmailAddress,
            ProviderTypeId: this.model.ProviderTypeId,
            TaxIdNumber: this.model.TaxIdNumber ? this.model.TaxIdNumber.replace('-', '') : null,
            PhysicalAddressId: this.model.PhysicalAddressId || null,
            MailingAddressId: this.model.MailingAddressId || null,
            LocationCoverageAddressId: this.model.LocationCoverageAddressId || null,
            IsAnchor: this.model.IsAnchor,
            AccountingName: this.model.AccountingName,
            Term: this.model.Term,
            MarketId: this.model.MarketId,
            AccountingMarketId: this.model.AccountingMarketId,
            AccountingMarketName: this.model.AccountingMarketName,
            AccountingVendorId: this.model.AccountingVendorId,
            IanaTimeZone: this.model.IanaTimeZone,
            BankingConfiguration: this.hasBankingConfigurationData()
              ? this.model.BankingConfiguration
              : null,
            InterchangeConfiguration: this.hasInterchangeConfigurationData()
              ? this.model.InterchangeConfiguration
              : null,
            PayerAddressConfiguration: this.hasPayerAddressData()
              ? this.model.PayerAddressConfiguration
              : null,
            PayerAdditionalInformationConfiguration: this.hasPayerAdditionalInformationConfigData()
              ? this.model.PayerAdditionalInformationConfiguration
              : null,
            PayerIdentificationConfiguration: this.model.PayerIdentificationConfiguration,
            PayerTechnicalContactInformationConfiguration: this.hasPayerTechnicalContactInformationConfigData()
              ? this.model.PayerTechnicalContactInformationConfiguration
              : null,
            PayeeAddressConfiguration:
              this.model && this.model.MailingAddress
                ? {
                    AddressLine1: this.model.MailingAddress.Address1,
                    AddressLine2: this.model.MailingAddress.Address2,
                    CityName: this.model.MailingAddress.CityName,
                    StateCode: response.data[0].Abbreviation,
                    StateId: this.model.MailingAddress.StateId,
                    PostalCode: this.model.MailingAddress.ZipCode
                  }
                : null,
            PayeeAdditionalInformationConfiguration: this.model
              ? {
                  ReferenceIdentificationQualifier: 'TJ',
                  ReferenceIdentification: this.model.TaxIdNumber
                }
              : null,
            PayeeIdentificationConfiguration: this.model
              ? {
                  Name: this.model.Name,
                  IdentificationCodeQualifier: 'XX',
                  IdentificationCode: this.model.Npi
                }
              : null,
            PeopleSoftOperatingUnit: this.model.PeopleSoftOperatingUnit,
            PeopleSoftVendorId: this.model.PeopleSoftVendorId,
            PeopleSoftLocationId: this.model.PeopleSoftLocationId,
            PeopleSoftOrgUnitName: this.model.PeopleSoftOrgUnitName
          } as IProvider
        }),
        switchMap(ediModel => {
          if (!this.model.Id) {
            ediModel.InterchangeConfiguration = null

            return this.odata.post(ediModel).exec()
          }
          return this.odata.customAction(this.model.Id, 'Default.UpdateDemographics', {
            dto: ediModel
          })
        })
      )
  }

  public updateAbbreviation(value: string) {
    this.model.Abbreviation = !!value ? value.toUpperCase() : null
  }

  private deleteOrphanedAddresses(provider): Observable<IProvider> {
    if (!this.model.PhysicalAddressId && this.physicalAddress.model.Id) {
      this.physicalAddress.deleteAddress()
    }

    if (!this.model.MailingAddressId && this.mailingAddress.model.Id) {
      this.mailingAddress.deleteAddress()
    }

    if (this.oldLocationCoverageId) {
      this.deleteOldLocationAddress()
    }

    return observableOf(provider)
  }

  private deleteOldLocationAddress() {
    this.providerService
      .query()
      .filter(
        `PhysicalAddressId eq ${this.oldLocationCoverageId} or MailingAddressId eq ${this.oldLocationCoverageId}`
      )
      .exec()
      .subscribe(response => {
        // If the old location coverage ID is not associated with a facility, abort the children
        if (!response.data[0]) {
          this.providerAddressService
            .delete(this.oldLocationCoverageId)
            .exec()
            .subscribe()

          this.oldLocationCoverageId = null
        }
      })
  }

  public save(): Observable<any> {
    const observables = [this.physicalAddress.save(), this.mailingAddress.save()]

    if (this.geographicToggle) {
      observables.push(this.locationCoverageAddress.saveProviderOverrideAddress())
    } else {
      observables.push(observableOf(null))
    }

    return forkJoin(observables).pipe(
      mergeMap(this.saveProvider.bind(this)),
      mergeMap(this.deleteOrphanedAddresses.bind(this))
    )
  }

  beforeValidation() {
    if (!this.mailingAddress && !this.physicalAddress) {
      return
    }

    if (this.mailingAddress.model && this.mailingAddress.model.ZipCode) {
      this.mailingAddress.model.ZipCode = this.mailingAddress.model.ZipCode.replace('-', '')
    }

    if (this.physicalAddress.model && this.physicalAddress.model.ZipCode) {
      this.physicalAddress.model.ZipCode = this.physicalAddress.model.ZipCode.replace('-', '')
    }

    if (
      this.locationCoverageAddress &&
      this.locationCoverageAddress.model &&
      this.locationCoverageAddress.model.ZipCode
    ) {
      this.locationCoverageAddress.model.ZipCode = this.locationCoverageAddress.model.ZipCode.replace(
        '-',
        ''
      )
    }
  }

  npiTypeSelected = (event: any) => {
    this.model.NpiTypeId = event.id
  }

  providerTypeSelected = (event: any) => {
    this.model.ProviderTypeId = event.id
  }

  timezoneSelected = (event: any) => {
    this.model.IanaTimeZone = event.id
  }
}
